/**
 * Hotfix 4 — Collapsible section wrapper for the Meal Plan Day Editor.
 *
 * Each top-level section header in the day editor (Regular Restaurants,
 * Specialty Restaurants, OVC Buffet, Crew Mess, Events, Special Events,
 * Canapé Function …) wraps its body in this component so the user can
 * click the chevron / title to hide the body and shrink the page.
 *
 * Collapsed/expanded state is persisted in `localStorage` keyed by
 * `vm:day_section:<sectionKey>`, NOT keyed per-voyage — the user's
 * collapse preferences apply to every voyage and every day. Default
 * state on first load is "expanded" (current behaviour).
 *
 * The body is **hidden via CSS** (display:none) when collapsed, NOT
 * conditionally rendered — that way any unsaved edits inside the body
 * survive a collapse/expand cycle intact.
 */
import { useState, useCallback } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const STORAGE_PREFIX = "vm:day_section:";

function readStored(sectionKey) {
  try {
    const v = localStorage.getItem(STORAGE_PREFIX + sectionKey);
    if (v === "1") return true;   // collapsed
    if (v === "0") return false;  // expanded
  } catch {
    /* localStorage unavailable — fall through to default. */
  }
  return false;  // default: expanded
}

function writeStored(sectionKey, collapsed) {
  try {
    localStorage.setItem(STORAGE_PREFIX + sectionKey, collapsed ? "1" : "0");
  } catch {
    /* no-op */
  }
}

export default function CollapsibleSection({
  sectionKey,
  title,
  className,
  headerExtra,
  children,
  testIdPrefix,
}) {
  const [collapsed, setCollapsed] = useState(() => readStored(sectionKey));
  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      writeStored(sectionKey, next);
      return next;
    });
  }, [sectionKey]);

  const tidBase = testIdPrefix || `day-section-${sectionKey}`;

  return (
    <section
      className={cn("rounded-lg border bg-card", className)}
      data-testid={tidBase}
      data-collapsed={collapsed ? "true" : "false"}
    >
      <button
        type="button"
        onClick={toggle}
        className={cn(
          "flex w-full items-center justify-between gap-2 px-4 py-3 text-left",
          "hover:bg-slate-50/80 transition-colors",
          // The header is always interactive — keep the body's rounded
          // corners visible underneath when expanded.
          collapsed ? "rounded-lg" : "rounded-t-lg border-b",
        )}
        aria-expanded={!collapsed}
        aria-controls={`${tidBase}-body`}
        data-testid={`${tidBase}-toggle`}
      >
        <span className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-slate-700">
          {collapsed ? (
            <ChevronRight className="h-4 w-4 text-slate-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-500" />
          )}
          {title}
        </span>
        {headerExtra && (
          <span
            className="ml-auto flex items-center gap-2"
            // Stop the button toggle when the user clicks something
            // interactive inside `headerExtra` (e.g. an action button).
            onClick={(e) => e.stopPropagation()}
          >
            {headerExtra}
          </span>
        )}
      </button>
      {/* Body — display:none when collapsed so unsaved edits stay
          mounted and intact (no conditional render that destroys state). */}
      <div
        id={`${tidBase}-body`}
        className="p-4"
        style={collapsed ? { display: "none" } : undefined}
      >
        {children}
      </div>
    </section>
  );
}
